/**
 * The talent dashboard's Excel exports (HR's requirements deck, 25 Sep 2026):
 *
 *   Reviews Scheduled                       a session a row
 *   Employee-level I&G Review report        a person in scope a row
 *   Integrated Review Actions / Decisions   a decision or action a row
 *
 * plus each view's own table. Every sheet is built from the same three reads as
 * the screens, with the same filters applied, so a sheet and a view agree.
 *
 * Gender and race are never written per person: the dashboard shows them only
 * as totals (HR, 9 Oct 2026).
 */
import type {
  ReportingAction,
  ReportingPerson,
  ReportingSession,
} from '@/services/api/talentReportingService'
import {
  MIDYEAR_RATING_LABEL,
  PERFORMANCE_SHORT,
  POTENTIAL_SHORT,
  RATING_PERIOD_SHORT,
  blockByNumber,
  type Level,
} from '@/components/performance/manager/talent-review/eduvosBlocks'
import { PLACEMENT_LABEL, READINESS_SHORT, mobilityOf, roleOf } from './talentDashboardData'
import type { RoleCoverage } from './talentRoleData'

export type SheetRow = Record<string, string | number>

const yes = (v: boolean | null | undefined) => (v ? 'Yes' : 'No')
const day = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : '')
const readiness = (r: string | null | undefined) => (r ? READINESS_SHORT[r as keyof typeof READINESS_SHORT] || r : '')
const level = (labels: Record<Level, string>, v: Level | null) => (v ? labels[v] : '')

export function historyLine(p: ReportingPerson): string {
  return (p.rating_history || [])
    .map((r) => `${r.review_year} ${RATING_PERIOD_SHORT[r.review_type] || r.review_type}: ${MIDYEAR_RATING_LABEL[r.rating] || r.label || r.rating}`)
    .join('; ')
}

export function successorLine(p: ReportingPerson): string {
  return (p.successor_for || []).map((s) => `${s.role} (${readiness(s.readiness)})`).join('; ')
}

/** What the rooms recorded about one person, one line per entry. */
export function actionsLine(actions: ReportingAction[]): string {
  return actions
    .map((a) => {
      const who = [a.responsible_name, a.due_date ? `due ${a.due_date}` : ''].filter(Boolean).join(', ')
      const detail = [a.what, a.summary].filter(Boolean).join(': ')
      return who ? `${detail} (${who})` : detail
    })
    .join('\n')
}

export function sessionsExportRows(sessions: ReportingSession[]): SheetRow[] {
  return sessions.map((s) => ({
    'Review date': day(s.date),
    Session: s.name || '',
    Function: s.chair_function || '',
    Department: s.chair_department || '',
    Chair: s.chair_name || '',
    'People Leaders invited': s.people_leaders.join('; '),
    'HR invited': s.hr.join('; '),
    Observers: s.observers.join('; '),
    'Lists handed in': s.lists_in,
    'Lists outstanding': Math.max(s.lists_total - s.lists_in, 0),
    'Placements made': s.placements_in,
    'Placements outstanding': s.placements_outstanding,
    Status: s.status,
  }))
}

export function employeeReportRows(people: ReportingPerson[], actions: ReportingAction[]): SheetRow[] {
  const byPerson = new Map<string, ReportingAction[]>()
  for (const a of actions) byPerson.set(a.employee_id, [...(byPerson.get(a.employee_id) || []), a])
  return people.map((p) => {
    const block = blockByNumber(p.block)
    return {
      'Employee number': p.employee_id,
      Name: p.name,
      Role: roleOf(p) || '',
      Function: p.function || '',
      Department: p.department || '',
      'Team 1': p.team_1 || '',
      'Team 2': p.team_2 || '',
      'Team 3': p.team_3 || '',
      'Leadership layer': p.leadership_layer || '',
      Grade: p.grade || '',
      'Critical role': yes(p.critical_role),
      'People Leader': p.leader_name || '',
      Session: p.session_name || '',
      Chair: p.chair_name || '',
      Placement: PLACEMENT_LABEL[p.placement_status],
      // 2026 is the first year potential is assessed, so it has no history yet.
      Potential: level(POTENTIAL_SHORT, p.potential),
      Impact: level(PERFORMANCE_SHORT, p.performance),
      Box: p.block ?? '',
      'Box label': block?.label || '',
      // Why they sit where they do, in the People Leader's words (HR, 7 Oct 2026:
      // the reasons could not be copied off the cards).
      'Placement reason': p.rationale || '',
      'Mid-year rating': p.midyear_rating ? MIDYEAR_RATING_LABEL[p.midyear_rating] || p.midyear_rating : '',
      'Impact history': historyLine(p),
      HIPO: yes(p.is_hipo),
      'Key person': yes(p.key_person),
      'Key person reason': p.key_person_reason || '',
      'Mitigating actions': p.mitigations || '',
      'Next role': p.next_role || '',
      'Readiness for next role': readiness(p.next_role_readiness),
      'Actions and development captured in the review': actionsLine(byPerson.get(p.employee_id) || []),
      Successor: yes(p.is_successor),
      'Successor for': successorLine(p),
      'Growth plan': yes(p.has_growth_plan),
      Mobility: mobilityOf(p) || '',
    }
  })
}

export function actionsExportRows(actions: ReportingAction[]): SheetRow[] {
  return actions.map((a) => ({
    'Review ID': a.session_id,
    Session: a.session_name || '',
    Date: day(a.session_date),
    Chair: a.chair_name || '',
    'Employee number': a.employee_id,
    Name: a.employee_name || '',
    'Decision or action': a.what,
    Detail: a.summary || '',
    'Moved from box': a.from_block ?? '',
    'Moved to box': a.to_block ?? '',
    'Growth plan update': a.growth_plan_item || '',
    Role: a.role || '',
    Readiness: readiness(a.readiness),
    'Responsible person': a.responsible_name || '',
    'Due date': a.due_date || '',
    'Recorded by': a.recorded_by || '',
    'Recorded at': a.recorded_at ? a.recorded_at.slice(0, 16).replace('T', ' ') : '',
  }))
}

/** The role views' rows: positions, the Ready Now people counted against the role,
 *  and its successors at each readiness, each with its share of the positions. */
export function roleExportRows(rows: RoleCoverage[], readyNow: Map<string, Set<string>>): SheetRow[] {
  return rows.map((c) => {
    const r = c.role
    const ready = readyNow.get(c.key)?.size || 0
    const share = (n: number) => (r.positions ? Math.round((n / r.positions) * 100) : '')
    return {
      Role: r.role_title,
      'Role code': r.role_code || '',
      Function: r.function || '',
      Department: r.department || '',
      'Team 1': r.team_1 || '',
      'Team 2': r.team_2 || '',
      'Team 3': r.team_3 || '',
      'Leadership layer': r.leadership_layer || '',
      Grade: r.grade || '',
      'Critical role': r.critical ? 'Yes' : 'No',
      Positions: r.positions,
      Headcount: r.filled,
      Vacancies: r.vacant,
      'Ready Now people': ready,
      'Bench strength (%)': share(ready),
      'Successors ready now': c.readyNow,
      'Ready now (% of positions)': share(c.readyNow),
      'Successors in 1 to 3 years': c.planned,
      '1 to 3 years (% of positions)': share(c.planned),
      'Successors in 3+ years': c.potential,
      '3+ years (% of positions)': share(c.potential),
      'Successors, any readiness': c.any,
      'Coverage (% of positions)': share(c.any),
    }
  })
}

/** One sheet, filter buttons on every column and the header row frozen. */
export async function downloadSheet(rows: SheetRow[], sheetName: string, fileStem: string): Promise<void> {
  const XLSX = await import('xlsx')
  const wb = XLSX.utils.book_new()
  const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ '': 'Nothing to export for these filters' }])
  const cols = Object.keys(rows[0] || { '': '' })
  sheet['!cols'] = cols.map((k) => ({ wch: Math.min(Math.max(k.length + 2, 14), 48) }))
  if (rows.length) {
    sheet['!autofilter'] = {
      ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length, c: Math.max(0, cols.length - 1) } }),
    }
  }
  sheet['!freeze'] = { xSplit: 0, ySplit: 1 }
  XLSX.utils.book_append_sheet(wb, sheet, sheetName.slice(0, 31))
  const stem = fileStem.replace(/[^\w]+/g, '-').replace(/^-|-$/g, '') || 'talent-dashboard'
  XLSX.writeFile(wb, `${stem}.xlsx`)
}
